/**
 * DataTable class
 *
 * @package    DataTable
 * @author     Maxim Lukianenko <mlukianenko@mindk.com>
 * @copyright  Copyright (c) 2010-2018 MindK (http://mindk.com). All rights reserved.
 * @license    http://mindk.com
 * @link       http://mindk.com
 */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import deepEqual from 'deep-equal';
import Table from '@material-ui/core/Table';
import Assignment from '@material-ui/icons/Assignment';

import GridContainer from 'Common/Grid/GridContainer.jsx';
import ItemGrid from 'Common/Grid/ItemGrid.jsx';
import IconCard from 'Common/Cards/IconCard.jsx';
import DataTableFilters from './DataTableFilters';
import DataTableHead from './DataTableHead';
import DataTableBody from './DataTableBody';
import DataTableCols from './DataTableCols';
import DataTableFooter from './DataTableFooter';

import {
    DATA_TABLE_COLUMN_TYPE_HIDDEN,
} from 'Constants';
import { DataTableColumnType, DataTableItemType, QueryFilterType, DataTableMetadataType } from 'Types';
import deepMerge from 'deepmerge';

export default class DataTable extends PureComponent {
    /**
     * PropTypes
     */
    static propTypes = {
        columns: PropTypes.arrayOf(DataTableColumnType).isRequired,
        data: PropTypes.arrayOf(DataTableItemType).isRequired,
        metadata: DataTableMetadataType,
        filter: QueryFilterType,
        load: PropTypes.func.isRequired,
        updateFilter: PropTypes.func.isRequired,
        onRowClick: PropTypes.func,
        title: PropTypes.string,
        showFilters: PropTypes.bool,
        icon: PropTypes.bool,
        separatedFilers: PropTypes.bool,
        additionalFilterBlock: PropTypes.any,
        sizedFilters: PropTypes.bool,
        search: PropTypes.bool,
        handleSearch: PropTypes.func,
        customCardContentClass: PropTypes.string,
        customCardClass: PropTypes.string,
        filtersKey: PropTypes.number,
        head: PropTypes.any,
        hideTitlesRow: PropTypes.bool,
        tableClass: PropTypes.string,
        customClass: PropTypes.string,
        footerMessage: PropTypes.any,
    };

    /**
     * DefaultProps
     */
    static defaultProps = {
        data: [],
        filter: {},
        showFilters: true,
        icon: true,
        separatedFilers: false,
        additionalFilterBlock: false,
        sizedFilters: false,
        search: false,
        head: null,
    };

    /**
     * Context Types
     *
     * @type Object
     */
    static contextTypes = {
        intl: PropTypes.object.isRequired,
    };

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps) {
        if (!deepEqual(prevProps.filter, this.props.filter)) {
            this.loadData();
        }
    }

    loadData = () => {
        const { load, filter } = this.props;
        load(filter);
    };

    setOrder = (property, direction) => {
        const { updateFilter } = this.props;
        updateFilter({ order: `${property} ${direction}` });
    };

    setWhere = (key, value, relation, filterKey, operator) => {
        const { updateFilter, filter: { where: currentWhere, has: currentHas } } = this.props;
        let where = { ...currentWhere };
        let has = { ...currentHas };
        key = filterKey ? filterKey : key;

        if (relation) {
            if (!value && this.isSetHasCondition(has, relation, key)) {
                if (Object.keys(has[relation].where).length === 1) {
                    delete has[relation];
                } else {
                    has = deepMerge(has, {[relation]: { where: {[key]: null}}});
                }
            } else if (value) {
                if (!has[relation] || !has[relation]['where']) {
                    has[relation] = {
                        'where': {}
                    };
                } else if (Object.keys(has[relation].where).length === 1) {
                    delete has[relation];
                }

                has = deepMerge(has, {[relation]: { where: { [key]: !Array.isArray(value) ? value : { inq: value } } }});
            }
        } else {
            if (where[key] && !value) {
                if (operator && where[key][operator]) {
                    let operatedWhere = {};
                    for (let operated in where[key]) {
                        if (operated !== operator) {
                            operatedWhere[operated] = where[key][operated];
                        }
                    }
                    where[key] = operatedWhere;
                } else {
                    delete where[key];
                }
            } else if (value) {
                let operatedValue;
                if (operator) {
                    operatedValue = {};
                    operatedValue[operator] = value;
                    if (!where[key]) {
                        where[key] = {};
                    }
                    where[key] = {...where[key], ...operatedValue};
                } else {
                    where[key] = !Array.isArray(value) ? value : { inq: value };
                }
            }
        }

        updateFilter({ where: where, has: has, page: 1 });
    };

    isSetHasCondition = (has, relation, key) => {
        return has[relation] && has[relation]['where'] && has[relation]['where'][key];
    };

    onChangePage = page => {
        const { updateFilter } = this.props;
        updateFilter({ page: page });
    };

    onChangeRowsPerPage = limit => {
        const { updateFilter } = this.props;
        updateFilter({ limit: limit, page: 1 });
    };

    getFiltersBlock = () => {
        const { columns, additionalFilterBlock} = this.props;
        const filterable = columns.some(item => item.filterable);

        return (filterable && (
            additionalFilterBlock ? (
                <GridContainer>
                    <ItemGrid
                        xs={ 12 }
                        sm={ 12 }
                        md={ 2 }
                        className={'left-filters-block'}
                    >
                        {additionalFilterBlock}
                    </ItemGrid>
                    <ItemGrid
                        xs={ 12 }
                        sm={ 12 }
                        md={ 9 }
                        className={'right-filters-block'}
                    >
                        {this.getFilters()}
                    </ItemGrid>
                </GridContainer>
            ) : this.getFilters()
        ));
    };

    getFilters = () => {
        const { columns, filter: { where, has }, showFilters, sizedFilters, search, handleSearch, filtersKey } = this.props;

        return (
            <div>
                <DataTableFilters
                    columns={columns.filter(item => item.filterable)}
                    where={where}
                    has={has}
                    setWhere={this.setWhere}
                    showFilters={showFilters}
                    sizedFilters={sizedFilters}
                    search={search}
                    handleSearch={handleSearch}
                    key={filtersKey}
                />
            </div>
        );
    };

    render() {
        const { columns, filter: { order }, data, metadata, onRowClick, title, icon, separatedFilers, showFilters,
            customCardContentClass, customCardClass, search, head, hideTitlesRow, tableClass, customClass, footerMessage } = this.props;
        const { intl: { formatMessage } } = this.context;

        return (
            <div className={customClass}>
                {separatedFilers &&
                    <GridContainer style={showFilters ? {} : {display: 'none'}} className={'separated-filters-block' +
                        (search ? ' separated-filters-block-search-wrapper' : ' separated-filters-block-wrapper')}>
                        <ItemGrid xs={12}>
                            <IconCard
                                icon={Assignment}
                                title={title ? formatMessage({ id: title }) : ''}
                                needIcon={icon}
                                content={
                                    <div>
                                        {this.getFiltersBlock()}
                                    </div>
                                }
                            />
                        </ItemGrid>
                    </GridContainer>
                }
                <GridContainer className={'data-table-main-content'}>
                    <ItemGrid xs={12}>
                        <IconCard
                            icon={Assignment}
                            title={title ? formatMessage({ id: title }) : ''}
                            needIcon={icon}
                            customCardContentClass={customCardContentClass}
                            classes={{ card: customCardClass }}
                            content={
                                <div>
                                    { head }
                                    {!separatedFilers && this.getFiltersBlock()}
                                    <Table className={tableClass}>
                                        <DataTableCols columns={columns.filter(column => column.type !== DATA_TABLE_COLUMN_TYPE_HIDDEN)} />
                                        {!hideTitlesRow && <DataTableHead columns={columns} order={order} setOrder={this.setOrder} />}
                                        <DataTableBody columns={columns} data={data} onRowClick={onRowClick} />
                                        <DataTableFooter
                                            metadata={metadata}
                                            onChangePage={this.onChangePage}
                                            onChangeRowsPerPage={this.onChangeRowsPerPage}
                                            footerMessage={footerMessage}
                                        />
                                    </Table>
                                </div>
                            }
                        />
                    </ItemGrid>
                </GridContainer>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/DataTable/DataTable.jsx